<template>
  <div class="formulario-modelo">
    <LoadingOverlay :visible="loading" message="Cargando pesos..." />
    <MensajeToast ref="toastRef" />
    <!-- Contenido normal -->
    <div v-if="!loading" class="marco">
      <div class="encabezado-flex">
        <div>
          <h2>Gestión de Pesos</h2>
          <p v-if="nombreModelo" class="ctx-modelo">{{ nombreModelo }}</p>
        </div>
        <div class="acciones-encabezado">
          <button class="btn salir" @click="volver">Volver</button>
          <button class="btn guardar"  @click="guardarCambios"  :disabled="loading" > {{ loading ? 'Guardando...' : 'Guardar Cambios' }}
          </button>
        </div>
      </div>
      <div class="divisor"></div>

      <!-- Sección de Filtros con Botones -->
      <div class="filtro-botones">
        <button
          :class="['btn-filtro', tipoFiltro === 'todos' ? 'activo' : '']"
          @click="tipoFiltro = 'todos'; componenteNivel1Filtro = null"
        >
          Mostrar todo el modelo
        </button>
        <button
          :class="['btn-filtro', tipoFiltro === 'solo-nivel-1' ? 'activo' : '']"
          @click="tipoFiltro = 'solo-nivel-1'; componenteNivel1Filtro = null"
        >
          Mostrar solo {{ nivelesArray[0] ?? 'Componentes' }}
        </button>
        <button
          v-if="nivelesArray.length >= 2"
          :class="['btn-filtro', tipoFiltro === 'nivel-1-y-2' ? 'activo' : '']"
          @click="tipoFiltro = 'nivel-1-y-2'; componenteNivel1Filtro = null"
        >
          {{ nivelesArray[0] ?? 'Nivel 1' }} y {{ nivelesArray[1] ?? 'Nivel 2' }}
        </button>
        <button
          :class="['btn-filtro', tipoFiltro === 'por-componente' ? 'activo' : '']"
          @click="tipoFiltro = 'por-componente'; componenteNivel2Filtro = null"
        >
          Por {{ nivelesArray[0] ?? 'Componentes' }}
        </button>
        <button
          v-if="todosComponentesNivel2.length > 0"
          :class="['btn-filtro', tipoFiltro === 'por-componente-n2' ? 'activo' : '']"
          @click="tipoFiltro = 'por-componente-n2'; componenteNivel1Filtro = null"
        >
          Por {{ nivelesArray[1] ?? 'Componentes' }}
        </button>
        <div class="decimales-control">
          <span class="decimales-label">Decimales:</span>
          <button class="btn-dec" :disabled="decimales <= 0" @click="decimales = Math.max(0, decimales - 1)">−</button>
          <span class="decimales-valor">{{ decimales }}</span>
          <button class="btn-dec" :disabled="decimales >= 4" @click="decimales = Math.min(4, decimales + 1)">+</button>
        </div>
      </div>

      <!-- Desplegable para filtrar por componente nivel 1 -->
      <div v-if="tipoFiltro === 'por-componente'" class="filtro-select">
        <label for="componente-filtro">Selecciona un {{ nivelesArray[0]?.toLowerCase() ?? 'componente' }}:</label>
        <select v-model.number="componenteNivel1Filtro" id="componente-filtro" class="form-control">
          <option :value="null">-- Selecciona --</option>
          <option v-for="comp in componentesNivel1" :key="comp.id_componente" :value="comp.id_componente">
            {{ formatOrdinal(comp.ordinal ?? '') }} - {{ comp.nombre }}
          </option>
        </select>
      </div>

      <!-- Desplegable para filtrar por componente nivel 2 -->
      <div v-if="tipoFiltro === 'por-componente-n2'" class="filtro-select">
        <label for="componente-n2-filtro">Selecciona un {{ nivelesArray[1]?.toLowerCase() ?? 'componente' }}:</label>
        <select v-model.number="componenteNivel2Filtro" id="componente-n2-filtro" class="form-control">
          <option :value="null">-- Selecciona --</option>
          <option v-for="comp in todosComponentesNivel2" :key="comp.id_componente" :value="comp.id_componente">
            {{ formatOrdinal(comp.ordinal ?? '') }} - {{ comp.nombre }}
          </option>
        </select>
      </div>
      <div class="divisor"></div>

      <div class="lista-componentes">
        <template v-for="comp in hijosRaiz" :key="comp.id_componente">
          <NodoComponente
            :componente="comp"
            :nivel="nivelBase"
            :loading="loading"
            :lockedMap="lockedMap"
            :componentes="componentes"
            :storeRaizId="storeComponentes.componentes[0]?.id_componente ?? null"
            :obtenerNumeracion="obtenerNumeracionJerarquica"
            :mostrarHijos="tipoFiltro !== 'solo-nivel-1'"
            :nivelMaximo="tipoFiltro === 'nivel-1-y-2' ? nivelBase + 1 : undefined"
            @limitar-decimales="limitarDecimales"
            @redistribuir="redistribuirPeso"
            @formatear="formatearDosDecimales"
            @update-peso="onUpdatePeso"
          />
        </template>
      </div>

      <div class="divisor"></div>
      <div class="acciones-encabezado">
        <button class="btn salir" @click="volver">Volver</button>
        <button class="btn guardar" @click="guardarCambios" :disabled="loading">
          {{ loading ? 'Guardando...' : 'Guardar Cambios' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Reparto de pesos entre hermanos del árbol.
 *
 * Los pesos se autodistribuyen mientras `auto_distribucion` esté activo; fijar
 * uno a mano lo desactiva para ese grupo. Las variantes que comparten
 * `grupo_variante` ocupan UN solo slot de peso.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/gestionPesos/:id_modelo` (Pesos) — permiso `modelos.editar`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComponentes } from '@/store/GestionComponentes'
import { useModelo } from '@/store/GestionModelo'
import type Componente from '@/interfaces/Componente'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { shallowRef } from 'vue'
import NodoComponente from '@/components/NodoComponente.vue'
import MensajeToast from '@/components/MensajeToast.vue'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { sumaColapsada, contarSlots } from '@/utils/pesosGrupo'

interface ComponenteAplanado {
  ref: Componente 
  nivel: number
}

const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)
const ruta = useRoute()
const router = useRouter()
const storeComponentes = useComponentes()
const modeloStore = useModelo()

const componentes = ref<ComponenteAplanado[]>([])
const idModelo = Number(ruta.params.id_modelo)
const nombreModelo = ref('')
const loading = ref(true)
const lockedMap = shallowRef<Map<number, boolean>>(new Map())

// Estados del filtro
const tipoFiltro = ref<'todos' | 'solo-nivel-1' | 'nivel-1-y-2' | 'por-componente' | 'por-componente-n2'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)
const componenteNivel2Filtro = ref<number | null>(null)
const nivelesArray = ref<string[]>([])
const decimales = ref(2)

// Función para obtener el nombre del nivel según su índice
function obtenerNombreNivel(nivelIndex: number): string {
  if (nivelIndex <= 0 || nivelIndex > nivelesArray.value.length) return ''
  return nivelesArray.value[nivelIndex - 1] ?? ''
}

// Proporcionar la función a través de inyección para los componentes hijos
provide('obtenerNombreNivel', obtenerNombreNivel)
provide('decimales', decimales)

// Marcar componente como modificado por usuario (congela su peso)
function marcarComoModificado(id?: number) {
  if (id == null) return
  const newMap = new Map(lockedMap.value)
  newMap.set(id, true)
  lockedMap.value = newMap
}

function estaModificado(id?: number): boolean {
  if (id == null) return false
  return !!lockedMap.value.get(id)
}

function onUpdatePeso({ comp, valor }: { comp: Componente; valor: number }) {
  comp.peso = valor
}

// Obtener todos los componentes de nivel 1 (directos del raíz)
function obtenerComponentesNivel1(): Componente[] {
  const raiz = storeComponentes.componentes[0]
  return [...(raiz?.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
}

// Obtener solo componentes de nivel 1 sin mostrar sus hijos
function obtenerComponentesNivel1SinHijos(): Componente[] {
  const raiz = storeComponentes.componentes[0]
  return [...(raiz?.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
}

// Obtener un componente con todos sus hijos recursivos
function obtenerComponenteConHijos(componenteId: number): Componente | null {
  function buscar(arbol: Componente[]): Componente | null {
    for (const comp of arbol) {
      if (comp.id_componente === componenteId) {
        return comp
      }
      if (comp.children?.length) {
        const resultado = buscar(comp.children)
        if (resultado) return resultado
      }
    }
    return null
  }

  const raiz = storeComponentes.componentes[0]
  return raiz?.children ? buscar(raiz.children) : null
}

// Obtener el nivel de un componente por su ID
function obtenerNivelComponente(componenteId: number): number {
  const aplanado = componentes.value.find(c => c.ref.id_componente === componenteId)
  return aplanado?.nivel ?? 1
}

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function precisionSignificativa(peso: number): number {
  for (let d = 0; d <= 4; d++) {
    if (Math.abs(peso - Number(peso.toFixed(d))) < 1e-9) return d
  }
  return 4
}

function detectarPrecisionArbol(nodos: Componente[]): number {
  let max = 0
  const scan = (c: Componente): void => {
    if (c.peso != null) max = Math.max(max, precisionSignificativa(c.peso))
    for (const h of c.children ?? []) scan(h)
  }
  for (const c of nodos) scan(c)
  return max
}

onMounted(async () => {
  try {
    loading.value = true
    if (!Number.isFinite(idModelo) || idModelo <= 0) {
      alert('ID de modelo inválido')
      return
    }

    const [raiz, modelo] = await Promise.all([
      storeComponentes.cargarRaizPorModelo(idModelo),
      modeloStore.getModeloById(idModelo)
    ])

    if (!raiz || !raiz.id_componente) {
      alert('No se encontró componente raíz para este modelo')
      return
    }

    nombreModelo.value = modelo?.nombre ?? ''
    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)

    const ok = await storeComponentes.cargarArbolDesdeRaiz(raiz.id_componente)
    if (!ok) {
      alert('Error al cargar árbol de componentes')
      return
    }

    componentes.value = aplanarComponentes(storeComponentes.componentes)

    const raizNodo = storeComponentes.componentes[0]
    if (raizNodo) decimales.value = detectarPrecisionArbol(raizNodo.children ?? [])
  } finally {
    loading.value = false
  }
})

const hijosRaiz = computed(() => {
  if (tipoFiltro.value === 'solo-nivel-1') {
    return obtenerComponentesNivel1SinHijos()
  }
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    const comp = obtenerComponenteConHijos(componenteNivel1Filtro.value)
    return comp ? [comp] : []
  }
  if (tipoFiltro.value === 'por-componente-n2' && componenteNivel2Filtro.value != null) {
    const comp = obtenerComponenteConHijos(componenteNivel2Filtro.value)
    return comp ? [comp] : []
  }
  return obtenerComponentesNivel1()
})

// Computeds para los desplegables de filtro
const componentesNivel1 = computed(() => obtenerComponentesNivel1())

const todosComponentesNivel2 = computed<Componente[]>(() => {
  const raiz = storeComponentes.componentes[0]
  const n2s: Componente[] = []
  for (const n1 of raiz?.children ?? []) {
    for (const n2 of n1.children ?? []) n2s.push(n2)
  }
  return n2s.sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
})

// Nivel base para los componentes mostrados
const nivelBase = computed(() => {
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    return obtenerNivelComponente(componenteNivel1Filtro.value)
  }
  if (tipoFiltro.value === 'por-componente-n2' && componenteNivel2Filtro.value != null) {
    return obtenerNivelComponente(componenteNivel2Filtro.value)
  }
  return 1
})

// Aplanar árbol con referencia directa (excluye nivel 0: raíz invisible)
function aplanarComponentes(arbol: Componente[], nivel = 0): ComponenteAplanado[] {
  const resultado: ComponenteAplanado[] = []
  for (const comp of arbol) {
    if (nivel > 0) {
      resultado.push({ ref: comp, nivel })
    }
    if ((comp.children?.length ?? 0) > 0) {
      resultado.push(...aplanarComponentes(comp.children!, nivel + 1))
    }
  }
  return resultado
}

// Helper: obtener referencia del padre desde el aplanado
function obtenerPadreRef(padreId: number | null | undefined): Componente | null {
  if (padreId == null) return null
  const padreAplanado = componentes.value.find(c => c.ref.id_componente === padreId)
  return padreAplanado?.ref ?? null
}

// Validaciones de input
function limitarDecimales(componente: Componente) {
  if (componente.peso != null) {
    if (componente.peso < 0) componente.peso = 0
    const partes = componente.peso.toString().split('.')
    if (partes[1]?.length > decimales.value) {
      componente.peso = parseFloat(`${partes[0]}.${partes[1].slice(0, decimales.value)}`)
    }
  }
}

function formatearDosDecimales(componente: Componente) {
  if (componente.peso != null && !isNaN(componente.peso)) {
    componente.peso = parseFloat(componente.peso.toFixed(decimales.value))
  }
}

// Redistribuir hijos respetando cuáles ya fueron modificados por el usuario
function redistribuirHijos(padre: Componente) {
  const hijos = padre.children ?? []
  if (!hijos.length) return
  const total = padre.padre_id == null ? 100 : (padre.peso ?? 0)

  // Separar hijos modificados (congelados) y libres
  const modificados = hijos.filter(h => estaModificado(h.id_componente))
  const libres = hijos.filter(h => !estaModificado(h.id_componente))

  // Calcular suma de modificados (variantes comparten slot)
  const sumaModificados = sumaColapsada(modificados)
  const restante = Math.max(0, total - sumaModificados)

  // Redistribuir entre libres: cada variante recibe el slot COMPLETO (no se divide
  // entre variantes), los hijos normales reciben una parte igual.
  if (libres.length > 0) {
    const numSlots = contarSlots(libres)
    if (numSlots > 0) {
      const nuevoPeso = parseFloat((restante / numSlots).toFixed(decimales.value))
      libres.forEach(h => {
        h.peso = nuevoPeso
        redistribuirHijos(h)
      })
    }
  }

  // Los modificados mantienen su peso, pero sus hijos se redistribuyen
  modificados.forEach(h => {
    redistribuirHijos(h)
  })
}

// Obtener numeración jerárquica (1, 1.1, 1.1.1, etc.)
function obtenerNumeracionJerarquica(componente: Componente, nivel: number): string {
  const aplanado = componentes.value.find(c => c.ref.id_componente === componente.id_componente)
  if (!aplanado) return ''
  
  const numeros: number[] = []
  let actual: ComponenteAplanado | null = aplanado
  
  // Construir la ruta desde el componente actual hasta la raíz
  while (actual && actual.nivel > 0) {
    const padre = obtenerPadreRef(actual.ref.padre_id)
    if (!padre) {
      // Encontrar posición entre hermanos en nivel 1
      const hermanos = componentes.value.filter(c => c.nivel === 1)
      const hermanosSorted = hermanos.sort((a, b) => 
        (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.ref.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      break
    } else {
      // Encontrar posición entre hermanos
      const hermanos = padre.children ?? []
      const hermanosSorted = [...hermanos].sort((a, b) => 
        (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      
      // Pasar al padre
      const actualAplanado = componentes.value.find(c => c.ref.id_componente === padre.id_componente)
      actual = actualAplanado ?? null
    }
  }
  
  return numeros.join('.')
}

// Evento principal de cambio
function redistribuirPeso(aplanado: ComponenteAplanado) {
  const nodo = aplanado.ref

  // Marcar este componente como modificado por el usuario
  marcarComoModificado(nodo.id_componente)

  // Si tiene hijos, redistribuye entre ellos respetando modificaciones
  if (nodo.children && nodo.children.length > 0) {
    redistribuirHijos(nodo)
  }

  // Redistribuir entre hermanos en el mismo nivel respetando modificaciones
  redistribuirEntreHermanos(aplanado)
}

// Redistribuir entre hermanos respetando cuáles fueron modificados
function redistribuirEntreHermanos(aplanado: ComponenteAplanado) {
  const nivel = aplanado.nivel
  const padreId = aplanado.ref.padre_id ?? null

  // Obtener hermanos en el mismo nivel
  const hermanosAplanados = componentes.value.filter(c =>
    c.nivel === nivel && (c.ref.padre_id ?? null) === padreId
  )

  if (hermanosAplanados.length <= 1) return

  // Obtener peso total permitido
  const padreRef = obtenerPadreRef(padreId)
  const totalPermitido = padreRef ? (padreRef.peso ?? 0) : 100

  // Separar hermanos modificados y libres
  const modificados = hermanosAplanados.filter(h => estaModificado(h.ref.id_componente))
  const libres = hermanosAplanados.filter(h => !estaModificado(h.ref.id_componente))

  // Calcular suma de modificados (variantes comparten slot)
  const sumaModificados = sumaColapsada(modificados.map(h => h.ref))
  const restante = Math.max(0, totalPermitido - sumaModificados)

  // Distribuir entre libres tratando el grupo de variantes como un único slot.
  if (libres.length > 0) {
    const numSlots = contarSlots(libres.map(h => h.ref))
    if (numSlots > 0) {
      const nuevoPeso = parseFloat((restante / numSlots).toFixed(decimales.value))
      libres.forEach(h => {
        h.ref.peso = nuevoPeso
        redistribuirHijos(h.ref)
      })
    }
  }
}

function validarPesosArbol(nodos: Componente[], totalEsperado: number): string | null {
  if (!nodos.length) return null
  // Las variantes por enfoque comparten un slot: cuentan una sola vez en la suma.
  const suma = sumaColapsada(nodos)
  const sumaRedondeada = parseFloat(suma.toFixed(decimales.value))
  const diferencia = Math.abs(sumaRedondeada - totalEsperado)
  if (diferencia > 0.5) {
    return `La suma de pesos (${sumaRedondeada}) no coincide con el total esperado (${totalEsperado}).`
  }
  for (const nodo of nodos) {
    if (nodo.children?.length) {
      const error = validarPesosArbol(nodo.children, nodo.peso ?? 0)
      if (error) return error
    }
  }
  return null
}

// Guardar cambios al backend
async function guardarCambios() {
  const arbolCompleto = obtenerComponentesNivel1()
  const errorValidacion = validarPesosArbol(arbolCompleto, 100)
  if (errorValidacion) {
    toastRef.value?.showToast('Los pesos no están correctos, revise la distribución antes de guardar.', 'error')
    return
  }

  try {
    loading.value = true

    // Guardar TODOS los nodos (ingresados y auto-distribuidos), usando los objetos
    // exactos del aplanado local — los mismos que modificaron redistribuirHijos y
    // redistribuirEntreHermanos — excluyendo la raíz que no tiene peso propio.
    const todos = componentes.value.map(a => a.ref)
    const resultados = await Promise.allSettled(todos.map(n => storeComponentes.actualizar(n)))
    const fallidos = resultados.filter(
      r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === null)
    ).length
    if (fallidos > 0) throw new Error(`${fallidos} componente(s) no pudieron guardarse.`)

    router.push({ name: 'ComponentesModelo', params: { id_modelo: idModelo } })
  } catch (err) {
    toastRef.value?.showToast('Error al guardar los pesos', 'error')
  } finally {
    loading.value = false
  }
}

function volver() {
  router.back()
}
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

.marco {
  border: 2px solid rgb(156, 155, 155);
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ctx-modelo {
  display: inline-block;
  margin: 6px 0 0;
  padding: 3px 10px;
  background: #e8f0f8;
  border-left: 3px solid #0e69a0;
  border-radius: 3px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111;
  letter-spacing: 0.01em;
}

.encabezado-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.acciones-encabezado {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.divisor {
  border-bottom: 1px solid #949393;
  margin: 12px 0 24px;
}

.filtro-botones {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.btn-filtro {
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: var(--font-family);
}

.btn-filtro:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.decimales-control {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 0.3rem 0.8rem;
}

.decimales-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #444;
}

.decimales-valor {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a1a;
  min-width: 1.2rem;
  text-align: center;
}

.btn-dec {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  padding: 0 0.2rem;
  line-height: 1;
}

.btn-dec:disabled {
  color: #bbb;
  cursor: not-allowed;
}

.filtro-select {
  margin-bottom: 1rem;
}

.filtro-select label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: var(--font-family);
  background-color: white;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.form-control:hover {
  border-color: #999;
}

.lista-componentes {
  font-family: var(--font-family);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-personalizado {
  width: 100px;
  max-width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  background-color: #f9f9f9;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
  min-width: 120px;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  cursor: pointer;
  color: white;
}

@media (max-width: 1024px) {
  .formulario-modelo {
    padding: 1rem;
    margin: 10px auto;
  }

  .encabezado-flex h2 {
    font-size: 1.4rem;
  }

  .btn {
    min-width: 100px;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
}
input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
</style>